/**
 * World transforms from a local pose (docs/SKEL-EDITOR.md §5.1 `construct.ts`, report 01 §6.4,
 * §6.7, §6.8): FK → IK → FK, then hidden propagation. Clean-room re-implementation of
 * rusty_skelform 0.8 `construct`/`inheritance` (MIT).
 *
 * FK, per bone in array order (parents first):
 *   rot   = (isFacingLeft(parent.scale) ? −local.rot : local.rot) + parent.rot
 *   scale = local.scale ⊙ parent.scale
 *   pos   = parent.pos + R(parent.rot)(local.pos ⊙ parent.scale)
 * i.e. world = T·R·S of the parent applied to the child's T·R(±)·S; shear from a non-uniform
 * parent scale is dropped, exactly like SkelForm. IK solves joint POSITIONS on a scratch copy of
 * pass 1, converts them to absolute world rotations, and a second FK pass lets those rotations
 * override the chain bones' (rusty only re-runs FK inside its physics pass — i.e. only when the
 * armature has physics — which is a bug the report's pipeline §6.4 does not have; we always re-run).
 * Physics is preserved in files but not simulated (D10), so there is no third pass.
 */
import { solveIk } from './ik'
import { add, div2, mul2, rot, shortestDelta, sub } from './math'
import type { Pose } from './animate'
import type { SkfRoot, SkfVisuals, V2 } from './types'

export interface WorldBone {
  pos: V2
  rot: number
  scale: V2
  hidden: boolean
}

export interface ConstructOptions {
  /** Run the IK pass (skipped anyway when `root.baked_ik` or there are no families). Default true. */
  ik?: boolean
}

/** (sx < 0) XOR (sy < 0): the frame is mirrored, so child rotations flip sign (report 01 §6.4). */
export function isFacingLeft(s: V2): boolean {
  return s.x < 0 !== s.y < 0
}

function fk(root: SkfRoot, pose: Pose, ikRots: Map<number, number> | null): WorldBone[] {
  const out: WorldBone[] = []
  const n = root.bones.length
  for (let i = 0; i < n; i++) {
    const def = root.bones[i]!
    const local = pose.bones[i]
    const lp = local?.pos ?? def.pos
    const ls = local?.scale ?? def.scale
    let r = local?.rot ?? def.rot
    let pos: V2 = { x: lp.x, y: lp.y }
    let scale: V2 = { x: ls.x, y: ls.y }
    const p = def.parent_id >= 0 && def.parent_id < i ? out[def.parent_id] : undefined
    if (p) {
      if (isFacingLeft(p.scale)) r = -r
      r += p.rot
      scale = mul2(scale, p.scale)
      pos = add(p.pos, rot(mul2(pos, p.scale), p.rot))
    }
    const ik = ikRots?.get(i)
    if (ik !== undefined) r = ik
    out.push({ pos, rot: r, scale, hidden: local?.hidden ?? def.hidden === true })
  }
  return out
}

/** Hidden bones hide their whole subtree (report 01 §6.8). Mutates `world`. */
export function propagateHidden(root: SkfRoot, world: WorldBone[]): void {
  for (let i = 0; i < world.length; i++) {
    const p = root.bones[i]?.parent_id ?? -1
    if (p >= 0 && p < i && world[p]!.hidden) world[i]!.hidden = true
  }
}

/** World pose of every bone (index-aligned with `root.bones`). */
export function construct(root: SkfRoot, pose: Pose, opts: ConstructOptions = {}): WorldBone[] {
  let world = fk(root, pose, null)
  if ((opts.ik ?? true) && !root.baked_ik && root.inverse_kinematics.length > 0) {
    const ikRots = solveIk(root, world, pose)
    if (ikRots.size > 0) world = fk(root, pose, ikRots)
  }
  propagateHidden(root, world)
  return world
}

// ------------------------------------------------------------------------------ inverse helpers

/**
 * The local position that puts bone `boneId`'s origin at `worldPos`: the inverse of its parent's
 * transform, `R(−parent.rot)(worldPos − parent.pos) ⊘ parent.scale`. Root bones: `worldPos`.
 */
export function worldToLocal(root: SkfRoot, world: WorldBone[], boneId: number, worldPos: V2): V2 {
  const pid = root.bones[boneId]?.parent_id ?? -1
  const p = pid >= 0 ? world[pid] : undefined
  if (!p) return { x: worldPos.x, y: worldPos.y }
  return div2(rot(sub(worldPos, p.pos), -p.rot), p.scale)
}

/** Inverse of `worldToLocal`: where a local position of `boneId` lands in the world. */
export function localToWorld(root: SkfRoot, world: WorldBone[], boneId: number, localPos: V2): V2 {
  const pid = root.bones[boneId]?.parent_id ?? -1
  const p = pid >= 0 ? world[pid] : undefined
  if (!p) return { x: localPos.x, y: localPos.y }
  return add(p.pos, rot(mul2(localPos, p.scale), p.rot))
}

/**
 * The local rotation giving bone `boneId` the world rotation `worldRot` (undoing the parent's
 * rotation and the facing-left negation). With `near`, the result is the angle closest to it
 * (`near + shortestDelta`), so a drag across ±π never makes keys jump a full turn.
 */
export function localRotForWorld(root: SkfRoot, world: WorldBone[], boneId: number, worldRot: number, near?: number): number {
  const pid = root.bones[boneId]?.parent_id ?? -1
  const p = pid >= 0 ? world[pid] : undefined
  let r = worldRot
  if (p) {
    r -= p.rot
    if (isFacingLeft(p.scale)) r = -r
  }
  return near === undefined ? r : near + shortestDelta(near, r)
}

/** Point in a bone's own frame (origin at the bone, its rotation and scale undone) → world. */
export function boneToWorld(bone: WorldBone, p: V2): V2 {
  return add(bone.pos, rot(mul2(p, bone.scale), bone.rot))
}

/** World point → a bone's own frame. */
export function worldToBone(bone: WorldBone, p: V2): V2 {
  return div2(rot(sub(p, bone.pos), -bone.rot), bone.scale)
}

// ----------------------------------------------------------------------------------- meshes

/** `inheritVert` (report 01 §6.7): a texture-centred local vertex → world, rigidly on `bone`. */
export function inheritVert(p: V2, bone: WorldBone, visual: SkfVisuals): V2 {
  const ps = visual.pivot_scale ?? { x: 1, y: 1 }
  const dir = isFacingLeft(bone.scale) ? -1 : 1
  return add(bone.pos, rot(mul2(mul2(p, bone.scale), ps), bone.rot + (visual.pivot_rot ?? 0) * dir))
}

/**
 * World positions of a mesh visual's vertices on bone `boneId` (report 01 §6.7: rigid follow of
 * the owner bone, then binds applied in order — weight binds lerp towards the bind bone, path
 * binds follow the averaged normal of the neighbouring bind bones). Does NOT add the pivot offset;
 * `draw.ts` adds it like the editor does. Returns null when the bone has no mesh.
 */
export function constructVerts(root: SkfRoot, world: WorldBone[], boneId: number): V2[] | null {
  const def = root.bones[boneId]
  const bone = world[boneId]
  const visual = def && def.visuals_id >= 0 ? root.visuals[def.visuals_id] : undefined
  if (!bone || !visual || !visual.vertices || visual.vertices.length === 0) return null
  const verts = visual.vertices.map((v) => inheritVert(v.init_pos ?? v.pos, bone, visual))
  const binds = visual.binds ?? []
  binds.forEach((bind, bi) => {
    if (bind.bone_id < 0) return
    const k = world[bind.bone_id]
    if (!k) return
    for (const bv of bind.verts) {
      const vert = visual.vertices![bv.id]
      const cur = verts[bv.id]
      if (!vert || !cur) continue
      const init = vert.init_pos ?? vert.pos
      if (!bind.is_path) {
        const target = inheritVert(init, k, visual)
        verts[bv.id] = { x: cur.x + (target.x - cur.x) * bv.weight, y: cur.y + (target.y - cur.y) * bv.weight }
        continue
      }
      const prevBind = binds[bi > 0 ? bi - 1 : bi]!
      const nextBind = binds[Math.min(bi + 1, binds.length - 1)]!
      const prev = world[prevBind.bone_id] ?? k
      const next = world[nextBind.bone_id] ?? k
      const pd = sub(k.pos, prev.pos)
      const nd = sub(next.pos, k.pos)
      const pn = unit({ x: -pd.y, y: pd.x })
      const nn = unit({ x: -nd.y, y: nd.x })
      const a = Math.atan2(pn.y + nn.y, pn.x + nn.x)
      const r = rot(init, a)
      verts[bv.id] = { x: k.pos.x + r.x * bv.weight, y: k.pos.y + r.y * bv.weight }
    }
  })
  return verts
}

function unit(v: V2): V2 {
  const m = Math.hypot(v.x, v.y)
  return m === 0 ? { x: 0, y: 0 } : { x: v.x / m, y: v.y / m }
}
